import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Logiciel de trésorerie : quel outil choisir en 2025 ? | Trezoone',
    description: 'Comparez les logiciels de trésorerie pour TPE, PME et associations. Trezoone vs Excel, outils comptables, solutions bancaires. Guide honnête pour bien choisir.',
    canonical: '/logiciel-tresorerie',
    keywords: 'logiciel trésorerie, logiciel gestion trésorerie, outil trésorerie, logiciel tresorerie pme, logiciel tresorerie association, meilleur logiciel tresorerie',
  },
  hero: {
    tag: 'Logiciel de trésorerie',
    title: 'Excel ou logiciel dédié ? Ce que personne ne vous dit vraiment.',
    subtitle: 'Le bon logiciel de trésorerie n\'est pas le plus cher ni le plus complexe. C\'est celui que vous utilisez vraiment, qui se connecte à votre banque, et qui vous dit quand vous allez avoir un problème — avant qu\'il arrive.',
    ctaLabel: 'Essayer Trezoone gratuitement',
  },
  pilierLink: {
    label: 'Guide complet : gestion de trésorerie',
    href: '/gestion-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'Le problème avec les outils actuels',
      content: 'La plupart des TPE, PME et associations gèrent leur trésorerie avec Excel. Ou pire : dans leur tête. Résultat :',
      items: [
        'Le fichier Excel n\'est plus à jour depuis 3 semaines, les formules sont cassées',
        'Le logiciel comptable donne une vision décalée : vous voyez le résultat, pas le cash',
        'Votre banque vous propose des outils, mais ils ne font que lire votre historique — pas prévoir l\'avenir',
        'Vous passez des heures à ressaisir des données au lieu de piloter votre activité',
      ],
    },
    {
      type: 'explain',
      title: 'Ce que doit faire un bon logiciel de trésorerie',
      content: 'Un logiciel de trésorerie efficace fait 4 choses fondamentales :',
      items: [
        '1. Se connecter à votre banque pour récupérer les données réelles automatiquement',
        '2. Vous permettre de saisir des prévisions (recettes attendues, charges à venir) facilement',
        '3. Calculer votre solde mois par mois et vous alerter sur les tensions à venir',
        '4. Vous fournir des rapports lisibles pour vos partenaires (banque, CA, financeurs)',
      ],
    },
    {
      type: 'midcta',
      title: 'Testez le seul logiciel conçu pour votre trésorerie, pas pour votre comptable',
      content: '14 jours gratuits · Import bancaire en 5 min · Sans engagement',
    },
    {
      type: 'example',
      title: 'Comparatif : Excel vs logiciel comptable vs Trezoone',
      content: 'Voici ce que font (ou ne font pas) les 3 options les plus courantes :',
      items: [
        'Excel : flexible, gratuit, mais aucune alerte, pas de connexion bancaire, erreurs fréquentes',
        'Logiciel comptable (Sage, Cegid…) : précis pour la compta, mais vision trésorerie secondaire, interface complexe',
        'Trezoone : connexion bancaire + plan prévisionnel + alertes + rapports → conçu pour la trésorerie uniquement',
        'Trezoone est accessible à un non-financier en moins de 30 minutes — contrairement aux ERP comptables',
      ],
    },
    {
      type: 'solution',
      title: 'Trezoone : le logiciel de trésorerie conçu pour le terrain',
      content: 'Pas de formation de 3 jours. Pas d\'intégration complexe. Trezoone est opérationnel en une demi-heure.',
      items: [
        'Import de relevés bancaires en un clic (CSV, OFX, PDF)',
        'Catégorisation automatique des transactions par IA',
        'Plan de trésorerie prévisionnel sur 12 mois',
        'Alertes configurables par seuil (ex : solde < 5 000 €)',
        'Module subventions pour associations et structures aidées',
        'Rapports PDF pour votre CA, banquier ou commissaire aux comptes',
        'Interface mobile pour suivre votre trésorerie partout',
        'À partir de 59 €/mois, sans engagement',
      ],
    },
    {
      type: 'faq',
      title: 'Questions sur le choix d\'un logiciel de trésorerie',
      faq: [
        {
          q: 'Un logiciel de trésorerie remplace-t-il un expert-comptable ?',
          a: 'Non. Un logiciel de trésorerie est un outil de pilotage opérationnel (cash disponible, tensions futures). Il complète votre comptable qui gère la vision fiscale et réglementaire. Les deux sont nécessaires.',
        },
        {
          q: 'Faut-il un logiciel de trésorerie si on a un petit budget ?',
          a: 'Oui, surtout dans ce cas. Les structures avec peu de marge d\'erreur ont besoin de visibilité maximale. Trezoone est accessible à partir de 59 €/mois — bien moins qu\'un découvert bancaire ou une heure de conseil urgente.',
        },
        {
          q: 'Trezoone fonctionne-t-il avec toutes les banques françaises ?',
          a: 'Trezoone accepte les relevés CSV, OFX et PDF de toutes les banques françaises. La connexion bancaire directe (Salt Edge) est disponible pour les principales banques.',
        },
        {
          q: 'Un logiciel de trésorerie est-il sécurisé ?',
          a: 'Trezoone utilise Supabase (infrastructure AWS) avec chiffrement des données, authentification sécurisée et isolation complète des données par utilisateur. Vos données financières ne sont jamais partagées.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Logiciels et outils par profil',
      links: [
        { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
        { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
        { label: 'Logiciel prévision trésorerie', href: '/logiciel-prevision-tresorerie' },
        { label: 'Outil prévision trésorerie', href: '/outil-prevision-tresorerie' },
        { label: 'Excel vs logiciel trésorerie', href: '/excel-vs-logiciel-tresorerie' },
        { label: 'Gestion trésorerie TPE', href: '/gestion-tresorerie-tpe' },
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Tableau trésorerie gratuit', href: '/tableau-tresorerie-gratuit' },
      ],
    },
    {
      type: 'cta',
      title: 'Testez Trezoone sans engagement',
      content: '14 jours gratuits. Importez vos données bancaires en 5 minutes. Voyez votre trésorerie sur 12 mois.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
    { label: 'Tarifs Trezoone', href: '/tarifs' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function LogicielTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
